import React, { useEffect, useRef, useState } from "react";
import {
  IDSButton,
  IDSCheckbox,
  IDSInput,
  IDSSpinner,
  IDSBadge,
  IDSCard,
  IDSTooltip,
} from "@inera/ids-react";

import Collapse from "../../Collapse";
import InfoTooltip from "../../common/InfoTooltip";

// Map a test result status to an IDSBadge for inline display per test case.
// The badge stays purely informational; navigation and next-step actions are
// kept as explicit controls so the row is readable and action-oriented.
const getStatusBadge = (status) => {
  if (!status) return null;
  let badge;
  if (status === "FINISHED") badge = <IDSBadge type="success">Lyckad</IDSBadge>;
  else if (status === "FAILED") badge = <IDSBadge type="error">Misslyckad</IDSBadge>;
  else if (status === "NOT_EXECUTED") badge = <IDSBadge type="attention">Ej körd</IDSBadge>;
  else badge = <IDSBadge type="info">{status}</IDSBadge>;

  return badge;
};

const getStatusMeta = (status) => {
  if (status === "FAILED") {
    return { action: "Visa", aria: "Visa resultat för" };
  }
  if (status === "NOT_EXECUTED") {
    return { action: null, aria: null };
  }
  if (status === "FINISHED") {
    return { action: "Visa", aria: "Visa resultat för" };
  }
  return { action: "Visa", aria: "Visa resultat för" };
};

const TYPE_PATTERNS = {
  datetime:    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/,
  date:        /^\d{8}$/,
  timestamp:   /^\d{14}$/,
  integer:     /^\d+$/,
  personnummer:/^\d{12}$/,
  oid:         /^[\d.]+$/,
  url:         /^https?:\/\//,
};

const TYPE_ERROR_MESSAGES = {
  datetime:    'Ogiltigt format. Förväntat: YYYY-MM-DDTHH:mm:ss',
  date:        'Ogiltigt format. Förväntat: YYYYMMDD (8 siffror)',
  timestamp:   'Ogiltigt format. Förväntat: ÅÅÅÅMMDDttmmss (14 siffror)',
  integer:     'Måste vara ett heltal',
  personnummer:'Måste vara 12 siffror utan bindestreck',
  oid:         'Ogiltigt OID-format (siffror separerade med punkter)',
  url:         'Måste börja med http:// eller https://',
};

const validateValue = (value, type) => {
  if (!value || !type || !TYPE_PATTERNS[type]) return true;
  return TYPE_PATTERNS[type].test(value);
};

const TestCaseSection = ({
  selectedContract,
  testSuites,
  contractChecked,
  setContractChecked,
  checkedSuites,
  setCheckedSuites,
  checkedCases,
  setCheckedCases,
  collapsedSuites,
  setCollapsedSuites,
  isLoading,
  onSaveAndRunTests,
  onRunSingleTestCase,
  runningCaseKey,
  selectedCount,
  resultStatusByKey,
  resultMessageByKey = {},
  resultHasJustificationByKey = {},
  onNavigateToResult,
  focusKey,
  focusNonce,
  testcaseParams,
  onTestcaseParamChange,
  paramMetadata = {},
}) => {
  const [collapsedCases, setCollapsedCases] = useState({});
  // Refs to each test case row (keyed by `${suiteIndex}-${caseIndex}`) so a
  // navigation from the result section can scroll the case into view.
  const caseRefs = useRef({});

  // Collapse all testcases by default when suites load
  useEffect(() => {
    if (!testSuites) return;
    const initialCollapse = {};
    testSuites.forEach((suite, sIndex) => {
      suite.testCases.forEach((_, cIndex) => {
        const caseKey = `${sIndex}-${cIndex}`;
        initialCollapse[caseKey] = true;
      });
    });
    setCollapsedCases(initialCollapse);
  }, [testSuites]);

  // React to a navigation from a test result: expand the containing suite and
  // the target case, then scroll it into view.
  useEffect(() => {
    if (!focusKey || !focusNonce) return;
    const [suiteIndex] = focusKey.split("-");
    setCollapsedSuites((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        next[Number(key)] = Number(key) === Number(suiteIndex) ? false : true;
      });
      return next;
    });
    setCollapsedCases((prev) => ({ ...prev, [focusKey]: false }));
    // Wait for the suite/case expand animations (~300ms) to finish before
    // scrolling, otherwise the target position is measured mid-animation.
    const timer = setTimeout(() => {
      const el = caseRefs.current[focusKey];
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 380);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey, focusNonce]);

  // Checkbox logic
  const handleMainCheckbox = (e) => {
    const isChecked = e.target.checked;
    setContractChecked(isChecked);
    const newSuites = {};
    const newCases = {};
    testSuites.forEach((suite, i) => {
      newSuites[i] = isChecked;
      newCases[i] = suite.testCases.map(() => isChecked);
    });
    setCheckedSuites(newSuites);
    setCheckedCases(newCases);
  };

  const handleSuiteChange = (index, isChecked) => {
    const updatedCases = testSuites[index].testCases.map(() => isChecked);
    setCheckedCases((prev) => ({ ...prev, [index]: updatedCases }));
    setCheckedSuites((prev) => ({ ...prev, [index]: isChecked }));
  };

  const handleCaseChange = (suiteIndex, caseIndex, isChecked) => {
    setCheckedCases((prev) => {
      const updated = { ...prev };
      const suiteCases = [...(updated[suiteIndex] || [])];
      suiteCases[caseIndex] = isChecked;
      updated[suiteIndex] = suiteCases;
      return updated;
    });
  };

  const toggleSuite = (suiteIndex) => {
    setCollapsedSuites((prev) => {
      const next = { ...prev };
      const currentlyCollapsed = !!prev[suiteIndex];
      Object.keys(next).forEach((key) => {
        const index = Number(key);
        next[index] = index === suiteIndex ? !currentlyCollapsed : true;
      });
      return next;
    });
  };

  return (
    <>
      {/* Main checkbox */}
      <div className="ids-mb-6">
        <IDSCheckbox
          key={`contract-${contractChecked}`}
          defaultChecked={contractChecked}
          onChange={handleMainCheckbox}
        >
          Välj alla för {selectedContract}
        </IDSCheckbox>
      </div>

      {/* Suites and testcases */}
      {testSuites.map((suite, sIndex) => (
        <IDSCard key={sIndex} className="ids-mb-6">
          {/* Suite header */}
          <div className="ids-flex" style={{ alignItems: 'center', gap: '0.5rem' }}>
            <button
              type="button"
              className="ids-btn-no-styles ids-mr-3"
              aria-label={`${collapsedSuites[sIndex] ? "Expandera" : "Fäll ihop"} ${suite.testSuiteName}`}
              aria-expanded={!collapsedSuites[sIndex]}
              onClick={() => toggleSuite(sIndex)}
            >
              {collapsedSuites[sIndex] ? (
                <span className="ids-icon-plus" aria-hidden="true" style={{ height: '1rem', width: '1rem' }} />
              ) : (
                <span className="ids-icon-minus" aria-hidden="true" style={{ height: '1rem', width: '1rem' }} />
              )}
            </button>

            <IDSCheckbox
              key={`suite-${sIndex}-${checkedSuites[sIndex] || false}`}
              defaultChecked={checkedSuites[sIndex] || false}
              onChange={(e) => handleSuiteChange(sIndex, e.target.checked)}
            >
              {suite.testSuiteName}
            </IDSCheckbox>
          </div>

          {/* Testcases */}
          <Collapse open={!collapsedSuites[sIndex]}>
            <ul className="ids-ml-6">
              {suite.testCases.map((testCaseName, cIndex) => {
                const testParam = testcaseParams.find(
                  (p) => p["@id"] === testCaseName
                );

                const caseKey = `${sIndex}-${cIndex}`;
                const isRunning = runningCaseKey === caseKey;
                const anyRunActive = isLoading || runningCaseKey !== null;
                const caseStatus =
                  resultStatusByKey?.[`${suite.testSuiteName}-${testCaseName}`] || 'NOT_EXECUTED';
                const hasJustification = !!resultHasJustificationByKey?.[`${suite.testSuiteName}-${testCaseName}`];
                const statusMeta = getStatusMeta(caseStatus);
                const hasResultDetails = !!resultStatusByKey?.[`${suite.testSuiteName}-${testCaseName}`];
                return (
                  <li
                    key={caseKey}
                    className="ids-mb-3"
                    style={{
                      borderTop: cIndex === 0 ? 'none' : '1px solid rgba(0, 0, 0, 0.12)',
                      paddingTop: cIndex === 0 ? '0' : '0.75rem',
                    }}
                    ref={(el) => {
                      if (el) caseRefs.current[caseKey] = el;
                    }}
                  >
                    <div className="ids-flex" style={{ alignItems: 'center', gap: '0.5rem', width: '100%', justifyContent: 'space-between' }}>
                      <div className="ids-flex" style={{ alignItems: 'center', gap: '0.5rem', minWidth: 0, flex: 1 }}>
                        <button
                         type="button"
                         className="ids-btn-no-styles ids-mr-3"
                         aria-label={`${collapsedCases[caseKey] ? "Expandera" : "Fäll ihop"} ${testCaseName}`}
                         aria-expanded={!collapsedCases[caseKey]}
                         onClick={() =>
                           setCollapsedCases((prev) => ({
                             ...prev,
                             [caseKey]: !prev[caseKey],
                           }))
                         }
                        >
                         {collapsedCases[caseKey] ? (
                           <span className="ids-icon-plus" aria-hidden="true" style={{ height: '0.9rem', width: '0.9rem' }} />
                         ) : (
                           <span className="ids-icon-minus" aria-hidden="true" style={{ height: '0.9rem', width: '0.9rem' }} />
                         )}
                        </button>

                        <IDSCheckbox
                         key={`case-${sIndex}-${cIndex}-${checkedCases[sIndex]?.[cIndex] || false}`}
                         defaultChecked={checkedCases[sIndex]?.[cIndex] || false}
                         onChange={(e) =>
                           handleCaseChange(sIndex, cIndex, e.target.checked)
                         }
                        >
                         {testCaseName}
                        </IDSCheckbox>
                      </div>

                      <div className="ids-flex" style={{ alignItems: 'center', gap: '0.5rem', flexWrap: 'nowrap', marginLeft: 'auto', justifyContent: 'flex-end' }}>
                        {caseStatus === 'FAILED' && hasJustification && (
                         <IDSTooltip
                           position="top-right"
                           style={{ position: 'relative', zIndex: 5000 }}
                           trigger={
                             <span
                               aria-label="Klar med motivering"
                               tabIndex={0}
                               title="Klar med motivering"
                               style={{
                                 display: 'inline-flex',
                                 alignItems: 'center',
                                 justifyContent: 'center',
                                 width: '1.25rem',
                                 height: '1.25rem',
                                 borderRadius: '999px',
                                 backgroundColor: '#dff6e8',
                                 color: '#1f7a45',
                                 fontSize: '0.8rem',
                                 fontWeight: 700,
                                 lineHeight: 1,
                                 cursor: 'pointer',
                               }}
                             >
                               ✓
                             </span>
                           }
                         >
                           <div style={{ maxWidth: '16rem', whiteSpace: 'normal' }}>Klar med motivering</div>
                         </IDSTooltip>
                        )}

                        {caseStatus && getStatusBadge(caseStatus)}

                        {onNavigateToResult && hasResultDetails && statusMeta.action && (
                         <IDSButton
                           size="s"
                           secondary
                           onClick={() => onNavigateToResult(suite.testSuiteName, testCaseName)}
                           aria-label={`${statusMeta.aria} ${testCaseName}`}
                         >
                           {statusMeta.action}
                         </IDSButton>
                        )}

                        <IDSButton
                         size="s"
                         secondary
                         disabled={anyRunActive}
                         onClick={() => {
                           setCollapsedCases((prev) => {
                             const next = { ...prev };
                             Object.keys(next).forEach((key) => {
                               if (key.startsWith(`${sIndex}-`)) {
                                 next[key] = true;
                               }
                             });
                             next[caseKey] = false;
                             return next;
                           });
                           onRunSingleTestCase(sIndex, cIndex);
                         }}
                         aria-label={`Kör ${testCaseName}`}
                         style={{ width: '2.5rem', minWidth: '2.5rem', padding: 0 }}
                        >
                         {isRunning ? (
                           <span
                             style={{
                               display: 'inline-flex',
                               alignItems: 'center',
                               justifyContent: 'center',
                               transform: 'scale(0.5)',
                               transformOrigin: 'center',
                             }}
                           >
                             <IDSSpinner size="s" />
                           </span>
                         ) : (
                           <span className="ids-icon-play" aria-hidden="true" style={{ height: '0.9rem', width: '0.9rem' }} />
                         )}
                        </IDSButton>
                      </div>
                    </div>

                    {/* Testcase description and parameters */}
                    <Collapse open={!collapsedCases[caseKey]}>
                      <div className="ids-ml-11">
                        {caseStatus === 'FAILED' && resultMessageByKey?.[`${suite.testSuiteName}-${testCaseName}`] && (
                          <div
                            className="ids-mt-3 ids-p-3"
                            style={{
                              backgroundColor: '#f6f1f1',
                              borderLeft: '4px solid #a61d15',
                              borderRadius: '0.25rem',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            }}
                          >
                            <strong>Testet misslyckades</strong>
                            <p style={{ margin: '0.5rem 0 0' }}>
                              {resultMessageByKey[`${suite.testSuiteName}-${testCaseName}`]}
                            </p>
                          </div>
                        )}
                        <p className="ids-mb-2 ids-mt-2" dangerouslySetInnerHTML={{ __html: testParam?.description || 'Ingen beskrivning tillgänglig' }} />                              
                        {testParam?.data && (
                          <div className="ids-flex" style={{ flexDirection: 'column', gap: '0.5rem' }}>
                                {Object.entries(testParam.data).map(([key, value]) => {
                              const meta = paramMetadata[key];
                              const isValid = validateValue(value, meta?.type);
                              return (
                              <div key={key} className="ids-mr-3">
                                <IDSInput
                                  label={<>{key}<InfoTooltip info={meta?.description} /></>}
                                  type="text"
                                  value={value || ""}
                                  placeholder={meta?.placeholder || ""}
                                  invalid={!isValid}
                                  errorMsg={!isValid ? TYPE_ERROR_MESSAGES[meta?.type] : undefined}
                                  onChange={(e) =>
                                    onTestcaseParamChange(
                                      testParam["@id"],
                                      key,
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </Collapse>
                  </li>
                );
              })}
            </ul>
          </Collapse>

        </IDSCard>
      ))}

      <div className="ids-mt-6 ids-flex" style={{ alignItems: 'center', gap: '1rem' }}>
        <IDSButton
          size="m"
          disabled={isLoading || runningCaseKey !== null}
          onClick={onSaveAndRunTests}
        >
          {isLoading ? (
            <IDSSpinner size="s" />
          ) : (
            "Kör valda tester"
          )}
        </IDSButton>
        {isLoading && (
          <span aria-live="polite">
            Kör {selectedCount} {selectedCount === 1 ? "test" : "tester"}…
          </span>
        )}
      </div>
    </>
  );
};

export default TestCaseSection;
